/**
 * @file Constants — publicationEpdFieldOptions. Mirrors backend `publicationEpdProgrammeOptions`. */

export const PUBLICATION_EPD_PROGRAMME_OPTIONS: { label: string; value: string }[] = [
  { label: "EPD Belgium", value: "EPD Belgium" },
  { label: "EPD China", value: "EPD China" },
  { label: "EPD Denmark", value: "EPD Denmark" },
  { label: "EPD Ireland", value: "EPD Ireland" },
  { label: "EPD-Norway", value: "EPD-Norway" },
  { label: "IBU", value: "IBU" },
  { label: "The Indian EPD Programme", value: "The Indian EPD Programme" },
  {
    label: "The International EPD® System",
    value: "The International EPD® System",
  },
];

/** Mirrors backend `publicationSystemBoundaryOptions`. */
export const PUBLICATION_SYSTEM_BOUNDARY_OPTIONS: { label: string; value: string }[] = [
  { label: "Cradle-to-Gate", value: "Cradle-to-Gate" },
  {
    label: "Cradle-to-Gate with Options",
    value: "Cradle-to-Gate with Options",
  },
  { label: "Cradle-to-Grave", value: "Cradle-to-Grave" },
  { label: "Cradle-to-Cradle", value: "Cradle-to-Cradle" },
];

const LEGACY_EPD_PROGRAMME: Record<string, string> = {
  "epd international": "The International EPD® System",
  "the international epd system": "The International EPD® System",
};

const LEGACY_SYSTEM_BOUNDARIES: Record<string, string> = {
  "cradle-to-gate": "Cradle-to-Gate",
  "cradle-to-gate with options": "Cradle-to-Gate with Options",
  "cradle-to-grave": "Cradle-to-Grave",
  "cradle-to-cradle": "Cradle-to-Cradle",
};

function findCanonicalOption(
  options: { label: string; value: string }[],
  raw: string
): { label: string; value: string } | undefined {
  return options.find(
    (o) => o.value === raw || o.label === raw || o.value.toLowerCase() === raw.toLowerCase()
  );
}

/** Exported function — normalize Epd Programme Stored Value. */
export function normalizeEpdProgrammeStoredValue(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const value = String(raw).trim();
  if (!value) return "";
  const canonical = findCanonicalOption(PUBLICATION_EPD_PROGRAMME_OPTIONS, value);
  if (canonical) return canonical.value;
  const legacy = LEGACY_EPD_PROGRAMME[value.toLowerCase()];
  return legacy ?? value;
}

/** Exported function — resolve Epd Programme Display Label. */
export function resolveEpdProgrammeDisplayLabel(raw: unknown): string {
  const value = normalizeEpdProgrammeStoredValue(raw);
  if (!value) return "";
  const canonical = findCanonicalOption(PUBLICATION_EPD_PROGRAMME_OPTIONS, value);
  return canonical?.label ?? value;
}

/** Exported function — normalize System Boundary Stored Value. */
export function normalizeSystemBoundaryStoredValue(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const value = String(raw).trim();
  if (!value) return "";
  const canonical = findCanonicalOption(PUBLICATION_SYSTEM_BOUNDARY_OPTIONS, value);
  if (canonical) return canonical.value;
  const legacy = LEGACY_SYSTEM_BOUNDARIES[value.toLowerCase()];
  return legacy ?? value;
}

/** Exported function — resolve System Boundary Display Label. */
export function resolveSystemBoundaryDisplayLabel(raw: unknown): string {
  const value = normalizeSystemBoundaryStoredValue(raw);
  if (!value) return "";
  const canonical = findCanonicalOption(PUBLICATION_SYSTEM_BOUNDARY_OPTIONS, value);
  return canonical?.label ?? value;
}

/** Exported function — normalize Reference Service Life Years. */
export function normalizeReferenceServiceLifeYears(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const value = String(raw).trim();
  if (!value) return "";
  const numeric = value.match(/^(\d+(?:\.\d+)?)/);
  return numeric ? numeric[1] : value;
}
